'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowUpRight, Check } from 'lucide-react'
import { PLANS } from './plans'
import { formatUsd } from '@/lib/formatNumber'

export default function PricingTierGrid() {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual')
  // LG-16: HistoryTruncationBanner (and checkout-failure fallbacks) deep-link
  // /pricing?upgrade=<slug>; previously nothing consumed the param, silently
  // dropping the purchase intent. Scroll to + highlight the target plan.
  // window.location (not useSearchParams) so the static prerender needs no
  // Suspense boundary.
  const [upgradeTarget, setUpgradeTarget] = useState<string | null>(null)
  useEffect(() => {
    const slug = new URLSearchParams(window.location.search).get('upgrade')
    if (!slug) return
    setUpgradeTarget(slug)
    const el = document.getElementById(`plan-${slug}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  return (
    <>
      <div className="mt-10 inline-flex items-center gap-1 bg-cream-50 border border-ink-900/10 rounded-full p-1">
        <button
          onClick={() => setBilling('monthly')}
          className={
            'px-5 py-2 rounded-full text-[14px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 ' +
            (billing === 'monthly'
              ? 'bg-terra-600 text-cream-50'
              : 'text-ink-700 hover:text-ink-900')
          }
        >
          Monthly
        </button>
        <button
          onClick={() => setBilling('annual')}
          className={
            'px-5 py-2 rounded-full text-[14px] font-medium transition-colors flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 ' +
            (billing === 'annual'
              ? 'bg-terra-600 text-cream-50'
              : 'text-ink-700 hover:text-ink-900')
          }
        >
          Annual
          <span
            className={
              'text-[12px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 ' +
              (billing === 'annual'
                ? 'bg-cream-50 text-terra-700'
                : 'bg-terra-100 text-terra-700')
            }
          >
            2 months free
          </span>
        </button>
      </div>

      <section className="pb-12 sm:pb-20 pt-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {PLANS.map((p) => {
            const price = billing === 'annual' ? p.annual : p.monthly
            return (
              <article
                key={p.name}
                id={`plan-${p.slug}`}
                className={
                  'bg-cream-50 border rounded-2xl p-6 sm:p-7 flex flex-col transition-colors ' +
                  (upgradeTarget === p.slug
                    ? 'ring-2 ring-terra-500/70 border-terra-500/60 ' : '') +
                  (p.featured
                    ? 'border-terra-400/60 ring-1 ring-inset ring-terra-400/40 bg-terra-50/40'
                    : 'border-ink-900/10 hover:border-ink-900/20')
                }
              >
                {/* Header row is reserved on every card so the featured
                    badge never pushes the price/divider down out of line. */}
                <div className="flex items-center justify-between min-h-[20px]">
                  <p className="mono text-eyebrow font-semibold uppercase tracking-widest text-ink-600">
                    {p.name}
                  </p>
                  {p.featured && (
                    <span className="mono text-caption font-semibold text-terra-700 uppercase tracking-widest">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="mt-4 leading-none">
                  <span className="text-h-lg font-semibold tracking-tight text-ink-900">
                    {formatUsd(price)}
                  </span>
                  <span className="text-h-sm text-ink-600 ml-1">
                    {price > 0 ? '/mo' : ''}
                  </span>
                </p>
                {/* Annual sub-copy row reserved on all cards so the
                    price -> features divider lands at the same baseline. */}
                <div className="mt-2 min-h-[22px] flex items-center gap-2">
                  {billing === 'annual' && p.monthly > 0 && (
                    <span className="text-caption text-ink-600">
                      Billed {formatUsd(p.annualTotal)}/yr
                    </span>
                  )}
                </div>
                {/* Fixed blurb height keeps the divider baseline identical
                    across all four cards regardless of blurb line count. */}
                <p className="mt-3 text-body-sm text-ink-700 leading-relaxed min-h-[48px]">
                  {p.blurb}
                </p>
                <ul className="mt-6 space-y-2 border-t border-ink-900/10 pt-5 flex-1">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-body-sm text-ink-700"
                    >
                      <Check className="w-3.5 h-3.5 text-terra-500 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  href={`${p.cta.href}${p.cta.href.includes('?') ? '&' : '?'}billing=${billing}`}
                  className={'mt-7 ' + (p.featured ? 'btn-primary' : 'btn-ghost')}
                >
                  {p.cta.label}
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </article>
            )
          })}
        </div>

        <p className="mt-8 text-caption text-ink-600 text-center uppercase tracking-widest">
          All paid plans, cancel anytime, annual saves 2 months
        </p>
      </section>
    </>
  )
}
